/**
 * Booking Service
 *
 * Core service for booking management including CRUD operations,
 * workflow transitions, payment tracking, and audit logging.
 *
 * NOTE: apiClient automatically transforms snake_case → camelCase in responses,
 * and camelCase → snake_case in request bodies. All backend response interfaces
 * use camelCase keys accordingly.
 *
 * Aligned with Django backend — see docs/api/BACKEND.md § Booking.
 */

import {
  Booking,
  BookingAccommodationSummary,
  BookingFormData,
  BookingGuestStayMovement,
  OpenStaysResponse,
  BookingGuestStaySummary,
  BookingIntegrationLine,
  BookingAuditEntry,
  BookingAuditAction,
  BookingStatus,
  PaymentStatus,
  CancellationRequest,
  BookingFilters,
  BookingDetail,
  PaginatedBookingsResponse,
  Folio,
  FolioPayment,
  RecordFolioPaymentInput,
  SettleBookingChargesInput,
  SettleBookingSplitInput,
  AddFolioChargeInput,
  ExpenseStatement,
  OwnershipInfo,
} from '../types';
import type { BookingFormDataInput } from '../validation';
import { apiClient } from '@/shared/services/api';
import { formatLocalDate } from '@/shared/utils/dateFormat';
import { isIntegrationSource } from '@/shared/utils/integrationOwnership';

// ---------------------------------------------------------------------------
// Backend response interfaces (camelCase — apiClient transforms keys)
// ---------------------------------------------------------------------------

/** Backend booking — list action (BookingListSerializer) */
interface BackendBookingList {
  id: string;
  guest: string;                 // UUID of guest
  guestName: string;             // guest.name
  room: string | null;           // UUID of room (nullable)
  roomName: string | null;       // room.name
  checkIn: string;               // date string "YYYY-MM-DD"
  checkOut: string;              // date string "YYYY-MM-DD"
  status: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show';
  adults: number;
  children: number;
  totalAmount: string;           // decimal as string
  paidAmount: string;            // decimal as string
  source: string;                // 'manual' | 'bookinglayer'
  externalId: string;            // BL booking UUID (empty if manual)
  externalReference: string;     // human-readable ref e.g. "2026-0001"
  currency: string;
  integrationSummary?: BackendIntegrationSummary | null;
  integration_summary?: BackendIntegrationSummary | null;
  localOverlaySummary?: BackendLocalOverlaySummary;
  local_overlay_summary?: BackendLocalOverlaySummary;
  unsyncedLocalOverlay?: boolean;
  unsynced_local_overlay?: boolean;
  ownership?: BackendOwnershipInfo;
  editPolicy?: BackendEditPolicy;
  edit_policy?: BackendEditPolicy;
  accommodationSummary?: string | null;
  accommodation_summary?: string | null;
  accommodationCount?: number;
  accommodation_count?: number;
  seasonInstance: string | null;
  seasonMappingStatus: 'matched' | 'season_unresolved' | 'external_override';
  crossesSeasonBoundary: boolean;
  seasonState: 'in_season' | 'out_of_season' | 'season_unresolved' | 'external_override';
  seasonPolicy: 'block' | 'warn' | 'allow';
  seasonInstanceLabel: string | null;
  overdueCheckout?: boolean;
  scheduledCheckInAt?: string | null;
  scheduledCheckOutAt?: string | null;
  additionalGuests?: BackendBookingGuestSummary[];
  additional_guests?: BackendBookingGuestSummary[];
}

/** Backend booking — detail action (BookingSerializer with fields='__all__') */
interface BackendBookingDetail extends BackendBookingList {
  specialRequests: string;
  notes: string;
  checkedInAt: string | null;
  checkedOutAt: string | null;
  createdAt: string;
  updatedAt: string;
  externalSeasonHint: string | null;
  seasonOverrideReason: string | null;
  seasonOverriddenBy: string | null;
  seasonOverriddenAt: string | null;
  bookingType: 'guest' | 'staff_guest' | 'day_use';
  sponsoredBy: string | null;
  dailyRate: string | null;
  /** Cancellation fee — populated when booking is cancelled with a fee. Not yet in backend serializer. */
  cancellationFee?: string | null;
  cancellation_fee?: string | null;
  cancellationReason?: string | null;
  cancellation_reason?: string | null;
  cancelledAt?: string | null;
  cancelled_at?: string | null;
  accommodations?: BackendBookingAccommodation[];
  integrationLines?: BackendIntegrationLine[];
  integration_lines?: BackendIntegrationLine[];
  integrationFinancialEvents?: BackendIntegrationFinancialEvent[];
  integration_financial_events?: BackendIntegrationFinancialEvent[];
  guestStays?: BackendBookingGuestStay[];
  guest_stays?: BackendBookingGuestStay[];
  additionalGuests?: BackendBookingGuestSummary[];
  additional_guests?: BackendBookingGuestSummary[];
}

interface BackendIntegrationSummary {
  total: string;
  paid: string;
  due: string;
  refund: string;
  currency?: string;
  status: string;
  externalUpdatedAt?: string | null;
  external_updated_at?: string | null;
}

interface BackendLocalOverlaySummary {
  total: string;
  paid: string;
  balance: string;
  currency?: string;
}

interface BackendOwnershipInfo {
  source: string;
  isIntegrationOwned?: boolean;
  is_integration_owned?: boolean;
  isLocal?: boolean;
  is_local?: boolean;
  label?: string;
  externalUrl?: string;
  external_url?: string;
}

interface BackendEditPolicy {
  canEditIntegratedFields?: boolean;
  can_edit_integrated_fields?: boolean;
  canEditLocalOverlay?: boolean;
  can_edit_local_overlay?: boolean;
  canEditNotes?: boolean;
  can_edit_notes?: boolean;
  canMutateLifecycle?: boolean;
  can_mutate_lifecycle?: boolean;
  canCheckinCheckout?: boolean;
  can_checkin_checkout?: boolean;
  canManageAccommodations?: boolean;
  can_manage_accommodations?: boolean;
}

interface BackendBookingGuestSummary {
  guest: string;
  guestName?: string;
  guest_name?: string;
  guestEmail?: string;
  guest_email?: string;
  isOwner?: boolean;
  is_owner?: boolean;
}

interface BackendBookingAccommodation {
  id: string;
  room?: string | null;
  roomName?: string | null;
  room_name?: string | null;
  roomType?: string | null;
  room_type?: string | null;
  roomTypeName?: string | null;
  room_type_name?: string | null;
  externalId?: string;
  external_id?: string;
  externalProductId?: string;
  external_product_id?: string;
  name?: string;
  startsAt?: string | null;
  starts_at?: string | null;
  endsAt?: string | null;
  ends_at?: string | null;
  status: 'reserved' | 'checked_in' | 'checked_out' | 'cancelled';
  quantity?: number;
  unitPrice?: string;
  unit_price?: string;
  totalPrice?: string;
  total_price?: string;
  ownership?: BackendOwnershipInfo;
  editPolicy?: BackendEditPolicy;
  edit_policy?: BackendEditPolicy;
}

interface BackendBookingGuestStay {
  id: string;
  booking: string;
  guest: string;
  bookingAccommodation?: string | null;
  booking_accommodation?: string | null;
  bookingAccommodationName?: string | null;
  booking_accommodation_name?: string | null;
  arrivalDate?: string | null;
  arrival_date?: string | null;
  departureDate?: string | null;
  departure_date?: string | null;
  scheduledCheckInAt?: string | null;
  scheduled_check_in_at?: string | null;
  scheduledCheckOutAt?: string | null;
  scheduled_check_out_at?: string | null;
  checkedInAt?: string | null;
  checked_in_at?: string | null;
  checkedOutAt?: string | null;
  checked_out_at?: string | null;
  status: 'expected' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show';
  waiverStatus?: 'not_required' | 'unsigned' | 'signed' | 'expired';
  waiverRequired?: boolean;
  waiverSignedAt?: string | null;
  waiverValidUntil?: string | null;
  waiverDocumentAvailable?: boolean;
  guestName?: string | null;
  guest_name?: string | null;
  bookingReference?: string | null;
  booking_reference?: string | null;
  bookingNumber?: string | null;
  booking_number?: string | null;
  ownership?: BackendOwnershipInfo;
  editPolicy?: BackendEditPolicy;
  edit_policy?: BackendEditPolicy;
}

interface BackendIntegrationLine {
  id: string;
  externalId?: string;
  external_id?: string;
  externalProductId?: string;
  external_product_id?: string;
  externalGuestId?: string;
  external_guest_id?: string;
  guest?: { id: string; name: string } | null;
  productType?: BookingIntegrationLine['productType'];
  product_type?: BookingIntegrationLine['productType'];
  status?: string;
  title?: string;
  description?: string;
  startsAt?: string | null;
  starts_at?: string | null;
  endsAt?: string | null;
  ends_at?: string | null;
  quantity?: string | number;
  unitPrice?: string;
  unit_price?: string;
  totalPrice?: string;
  total_price?: string;
  currency?: string;
}

interface BackendIntegrationFinancialEvent {
  id: string;
  eventType?: 'payment' | 'refund' | 'credit_note' | 'invoice';
  event_type?: 'payment' | 'refund' | 'credit_note' | 'invoice';
  externalId?: string;
  external_id?: string;
  parentExternalId?: string;
  parent_external_id?: string;
  externalGuestId?: string;
  external_guest_id?: string;
  status?: string;
  amount?: string;
  currency?: string;
  paymentGatewayTitle?: string;
  payment_gateway_title?: string;
  eventAt?: string | null;
  event_at?: string | null;
  reference?: string;
  comment?: string;
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

type BackendStatus = BackendBookingList['status'];

function mapStatus(s: BackendStatus): BookingStatus {
  const map: Record<BackendStatus, BookingStatus> = {
    pending: 'pending',
    confirmed: 'confirmed',
    checked_in: 'checked-in',
    checked_out: 'checked-out',
    cancelled: 'cancelled',
    no_show: 'no-show',
  };
  return map[s];
}

function toBackendStatus(s: BookingStatus): string {
  return s.replace('-', '_');
}

function toBackendPaymentStatus(s: PaymentStatus): string {
  return s.replace('-', '_');
}

function formatDateParam(value: Date): string {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function derivePaymentStatus(total: number, paid: number): PaymentStatus {
  if (total <= 0 && paid <= 0) return 'paid';
  if (paid <= 0) return 'unpaid';
  if (paid >= total) return 'paid';
  return 'partially-paid';
}

function getDisplayedAmounts(
  integrationSummary: ReturnType<typeof mapIntegrationSummary>,
  localOverlaySummary: ReturnType<typeof mapLocalOverlaySummary>,
  totalAmount: string,
  paidAmount?: string,
) {
  if (integrationSummary) {
    return {
      total: integrationSummary.total,
      paid: integrationSummary.paid,
    };
  }
  if (localOverlaySummary) {
    return {
      total: localOverlaySummary.total,
      paid: localOverlaySummary.paid,
    };
  }
  return {
    total: parseFloat(totalAmount) || 0,
    paid: parseFloat(paidAmount ?? '0') || 0,
  };
}

function parseDateOnly(value?: string | null): Date | null {
  if (!value) return null;
  return new Date(`${value.slice(0, 10)}T00:00:00`);
}

function parseDateTime(value?: string | null): Date | null {
  if (!value) return null;
  return new Date(value);
}

/** Parse cancellation fee from backend (decimal string or null). Returns undefined when absent. */
function parseCancellationFee(value?: string | null): number | undefined {
  if (value == null) return undefined;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? undefined : parsed;
}

function mapBackendAccommodation(
  accommodation: BackendBookingAccommodation,
): BookingAccommodationSummary {
  return {
    id: accommodation.id,
    externalId: accommodation.externalId ?? undefined,
    externalProductId: accommodation.externalProductId ?? undefined,
    roomId: accommodation.room ?? null,
    roomTypeId: accommodation.roomType ?? null,
    roomName: accommodation.roomName ?? null,
    roomTypeName: accommodation.roomTypeName ?? null,
    name:
      accommodation.name
      ?? accommodation.room_name
      ?? accommodation.roomName
      ?? '',
    startsAt: parseDateOnly(accommodation.startsAt),
    endsAt: parseDateOnly(accommodation.endsAt),
    status: accommodation.status,
    quantity: accommodation.quantity ?? 1,
    unitPrice: parseFloat(accommodation.unitPrice ?? '0') || 0,
    totalPrice: parseFloat(accommodation.totalPrice ?? '0') || 0,
    ownership: mapOwnership(accommodation.ownership),
    editPolicy: mapEditPolicy(accommodation.editPolicy),
  };
}

function mapBackendGuestStay(stay: BackendBookingGuestStay): BookingGuestStaySummary {
  return {
    id: stay.id,
    guestId: stay.guest,
    accommodationId: stay.bookingAccommodation ?? null,
    accommodationName: stay.bookingAccommodationName ?? null,
    arrivalDate: parseDateOnly(stay.arrivalDate),
    departureDate: parseDateOnly(stay.departureDate),
    scheduledCheckInAt: parseDateTime(stay.scheduledCheckInAt),
    scheduledCheckOutAt: parseDateTime(stay.scheduledCheckOutAt),
    checkedInAt: parseDateTime(stay.checkedInAt),
    checkedOutAt: parseDateTime(stay.checkedOutAt),
    status: stay.status,
    waiverStatus: stay.waiverStatus,
    waiverRequired: stay.waiverRequired,
    waiverSignedAt: parseDateTime(stay.waiverSignedAt),
    waiverValidUntil: parseDateTime(stay.waiverValidUntil),
    waiverDocumentAvailable: stay.waiverDocumentAvailable,
    ownership: mapOwnership(stay.ownership),
    editPolicy: mapEditPolicy(stay.editPolicy),
  };
}

function mapOwnership(ownership?: BackendOwnershipInfo): OwnershipInfo | undefined {
  if (!ownership) return undefined;
  // The source is carried through verbatim. This used to collapse anything
  // that was not 'bookinglayer' into 'manual', which would have labelled a
  // second integration's records as locally owned — i.e. editable — and every
  // edit to them would be overwritten on the next sync.
  const source = ownership.source;
  return {
    source,
    // Derive the flag when the backend omits it rather than defaulting to
    // `false`: "we were not told" is not the same as "this is ours".
    isIntegrationOwned: ownership.isIntegrationOwned ?? isIntegrationSource(source),
    isLocal: ownership.isLocal ?? !isIntegrationSource(source),
    // Supplied by whichever integration owns the record — the deck neither
    // knows nor needs to know whose address this is.
    label: ownership.label ?? '',
    externalUrl: ownership.externalUrl ?? ownership.external_url ?? '',
  };
}

function mapEditPolicy(editPolicy?: BackendEditPolicy) {
  if (!editPolicy) return undefined;
  return {
    canEditIntegratedFields: editPolicy.canEditIntegratedFields ?? false,
    canEditLocalOverlay: editPolicy.canEditLocalOverlay,
    canEditNotes: editPolicy.canEditNotes,
    canMutateLifecycle: editPolicy.canMutateLifecycle,
    canCheckinCheckout: editPolicy.canCheckinCheckout,
    canManageAccommodations: editPolicy.canManageAccommodations,
  };
}

function mapIntegrationSummary(summary?: BackendIntegrationSummary | null) {
  if (!summary) return null;
  return {
    total: parseFloat(summary.total ?? '0') || 0,
    paid: parseFloat(summary.paid ?? '0') || 0,
    due: parseFloat(summary.due ?? '0') || 0,
    refund: parseFloat(summary.refund ?? '0') || 0,
    currency: summary.currency,
    status: summary.status,
    externalUpdatedAt: parseDateTime(summary.externalUpdatedAt),
  };
}

function mapLocalOverlaySummary(summary?: BackendLocalOverlaySummary | null) {
  if (!summary) return undefined;
  return {
    total: parseFloat(summary.total ?? '0') || 0,
    paid: parseFloat(summary.paid ?? '0') || 0,
    balance: parseFloat(summary.balance ?? '0') || 0,
    currency: summary.currency,
  };
}

function mapIntegrationLines(lines?: BackendIntegrationLine[]) {
  return (lines ?? []).map((line) => ({
    id: line.id,
    externalId: line.externalId ?? '',
    externalProductId: line.externalProductId,
    externalGuestId: line.externalGuestId,
    guest: line.guest ?? null,
    productType: line.productType ?? 'unknown',
    status: line.status ?? '',
    title: line.title ?? '',
    description: line.description,
    startsAt: parseDateTime(line.startsAt),
    endsAt: parseDateTime(line.endsAt),
    quantity: typeof line.quantity === 'number' ? line.quantity : parseFloat(String(line.quantity ?? '0')) || 0,
    unitPrice: parseFloat(line.unitPrice ?? '0') || 0,
    totalPrice: parseFloat(line.totalPrice ?? '0') || 0,
    currency: line.currency,
  }));
}

function mapIntegrationFinancialEvents(events?: BackendIntegrationFinancialEvent[]) {
  return (events ?? []).map((event) => ({
    id: event.id,
    eventType: event.eventType ?? 'invoice',
    externalId: event.externalId ?? '',
    parentExternalId: event.parentExternalId,
    externalGuestId: event.externalGuestId,
    status: event.status ?? '',
    amount: parseFloat(event.amount ?? '0') || 0,
    currency: event.currency,
    paymentGatewayTitle: event.paymentGatewayTitle,
    eventAt: parseDateTime(event.eventAt),
    reference: event.reference,
    comment: event.comment,
  }));
}

function buildAccommodationSummary(
  accommodations: BookingAccommodationSummary[],
  fallbackRoomName?: string | null,
): string | undefined {
  if (accommodations.length === 0) {
    return fallbackRoomName || undefined;
  }

  const names = Array.from(new Set(
    accommodations
      .map((accommodation) => accommodation.name || accommodation.roomName || '')
      .filter(Boolean),
  ));

  if (names.length === 0) {
    return fallbackRoomName || undefined;
  }
  if (names.length === 1) {
    return names[0];
  }
  return `${names[0]} +${names.length - 1}`;
}

function buildBookingGuests(
  ownerGuestId: string | null | undefined,
  ownerGuestName: string | undefined,
  additionalGuests: BackendBookingGuestSummary[],
  occupantGuestIds?: ReadonlySet<string>,
) {
  const guestsById = new Map<string, Booking['guests'][number]>();

  if (ownerGuestId) {
    guestsById.set(ownerGuestId, {
      guestId: ownerGuestId,
      isOwner: true,
      guestName: ownerGuestName || undefined,
    });
  }

  for (const guest of additionalGuests) {
    const guestId = guest.guest;
    if (!guestId) continue;

    const existing = guestsById.get(guestId);
    const mappedGuest = {
      guestId,
      guestName: guest.guestName,
      guestEmail: guest.guestEmail,
      isOwner: guest.isOwner ?? false,
    };

    guestsById.set(guestId, {
      ...existing,
      ...mappedGuest,
      isOwner: existing?.isOwner || mappedGuest.isOwner,
      guestName: existing?.guestName || mappedGuest.guestName,
      guestEmail: existing?.guestEmail || mappedGuest.guestEmail,
    });
  }

  const guests = Array.from(guestsById.values());

  // The booker is not necessarily a guest: agency/agent bookers appear as the
  // booking's primary `guest` but never occupy a stay. When we know occupancy
  // (at least one guest stay exists), the booker counts as a guest only if they
  // actually occupy a stay — otherwise they belong in "Booking information",
  // not in the guest roster. With no stays (unsynced/manual bookings) we can't
  // tell, so we keep the current best-effort roster untouched.
  if (occupantGuestIds && occupantGuestIds.size > 0) {
    return guests.filter((g) => !g.isOwner || occupantGuestIds.has(g.guestId));
  }

  return guests;
}

function mapBackendBooking(b: BackendBookingDetail): Booking {
  const integrationSummary = mapIntegrationSummary(b.integrationSummary ?? null);
  const localOverlaySummary = mapLocalOverlaySummary(b.localOverlaySummary);
  const { total, paid } = getDisplayedAmounts(
    integrationSummary,
    localOverlaySummary,
    b.totalAmount,
    b.paidAmount,
  );
  const now = new Date();
  const additionalGuests = b.additionalGuests ?? b.additional_guests ?? [];
  const accommodations = (b.accommodations ?? []).map(mapBackendAccommodation);
  const guestStays = (b.guestStays ?? b.guest_stays ?? []).map(mapBackendGuestStay);
  const occupantGuestIds = new Set(guestStays.map((stay) => stay.guestId));
  const accommodationSummary = buildAccommodationSummary(accommodations, b.roomName);
  return {
    id: b.id,
    bookingNumber: b.externalReference || b.id.slice(0, 8).toUpperCase(),
    guests: buildBookingGuests(b.guest, b.guestName || undefined, additionalGuests, occupantGuestIds),
    accommodationId: accommodations[0]?.id ?? b.room ?? '',
    accommodations,
    guestStays,
    checkInDate: new Date(b.checkIn + 'T00:00:00'),
    checkOutDate: new Date(b.checkOut + 'T00:00:00'),
    status: mapStatus(b.status),
    paymentStatus: derivePaymentStatus(total, paid),
    totalAmount: total,
    paidAmount: paid,
    integrationSummary,
    localOverlaySummary,
    unsyncedLocalOverlay: b.unsyncedLocalOverlay ?? false,
    ownership: mapOwnership(b.ownership),
    editPolicy: mapEditPolicy(b.editPolicy),
    adults: b.adults,
    children: b.children,
    specialRequests: b.specialRequests || undefined,
    currency: b.currency || undefined,
    guestName: b.guestName || undefined,
    roomName: b.roomName || accommodations[0]?.roomName || accommodations[0]?.name || undefined,
    accommodationSummary,
    seasonInstanceId: b.seasonInstance || undefined,
    seasonInstanceLabel: b.seasonInstanceLabel || undefined,
    seasonMappingStatus: b.seasonMappingStatus,
    seasonState: b.seasonState,
    seasonPolicy: b.seasonPolicy,
    crossesSeasonBoundary: b.crossesSeasonBoundary,
    externalSeasonHint: b.externalSeasonHint || undefined,
    seasonOverrideReason: b.seasonOverrideReason || undefined,
    seasonOverriddenBy: b.seasonOverriddenBy || undefined,
    seasonOverriddenAt: b.seasonOverriddenAt ? new Date(b.seasonOverriddenAt) : undefined,
    notes: b.notes ?? undefined,
    cancellationFee: parseCancellationFee(b.cancellationFee),
    cancellationReason: (b.cancellationReason) || undefined,
    cancelledAt: parseDateTime(b.cancelledAt) || undefined,
    source: (b.source || 'manual') as import('@/shared/types').SyncSource,
    externalId: b.externalId || undefined,
    externalReference: b.externalReference || undefined,
    createdAt: b.createdAt ? new Date(b.createdAt) : now,
    updatedAt: b.updatedAt ? new Date(b.updatedAt) : now,
    bookingType: b.bookingType ?? 'guest',
    sponsoredBy: b.sponsoredBy ?? null,
    dailyRate: b.dailyRate ? parseFloat(b.dailyRate) : null,
  };
}

/** Lightweight mapper for list responses */
function mapBackendBookingList(b: BackendBookingList): Booking {
  const integrationSummary = mapIntegrationSummary(b.integrationSummary ?? null);
  const localOverlaySummary = mapLocalOverlaySummary(b.localOverlaySummary);
  const { total, paid } = getDisplayedAmounts(
    integrationSummary,
    localOverlaySummary,
    b.totalAmount,
    b.paidAmount,
  );
  const now = new Date();
  const additionalGuests = b.additionalGuests ?? b.additional_guests ?? [];
  return {
    id: b.id,
    bookingNumber: b.externalReference || b.id.slice(0, 8).toUpperCase(),
    guests: buildBookingGuests(b.guest, b.guestName || undefined, additionalGuests),
    accommodationId: b.room ?? '',
    checkInDate: new Date(b.checkIn + 'T00:00:00'),
    checkOutDate: new Date(b.checkOut + 'T00:00:00'),
    status: mapStatus(b.status),
    paymentStatus: derivePaymentStatus(total, paid),
    totalAmount: total,
    paidAmount: paid,
    integrationSummary,
    localOverlaySummary,
    unsyncedLocalOverlay: b.unsyncedLocalOverlay ?? false,
    ownership: mapOwnership(b.ownership),
    editPolicy: mapEditPolicy(b.editPolicy),
    adults: b.adults,
    children: b.children,
    guestName: b.guestName || undefined,
    roomName: b.roomName || undefined,
    accommodationSummary: b.accommodationSummary ?? b.roomName ?? undefined,
    source: (b.source || 'manual') as import('@/shared/types').SyncSource,
    externalId: b.externalId || undefined,
    externalReference: b.externalReference || undefined,
    currency: b.currency || undefined,
    seasonInstanceId: b.seasonInstance || undefined,
    seasonInstanceLabel: b.seasonInstanceLabel || undefined,
    seasonMappingStatus: b.seasonMappingStatus,
    seasonState: b.seasonState,
    seasonPolicy: b.seasonPolicy,
    crossesSeasonBoundary: b.crossesSeasonBoundary,
    overdueCheckout: b.overdueCheckout ?? false,
    scheduledCheckInAt: b.scheduledCheckInAt ? new Date(b.scheduledCheckInAt) : null,
    scheduledCheckOutAt: b.scheduledCheckOutAt ? new Date(b.scheduledCheckOutAt) : null,
    createdAt: now,
    updatedAt: now,
  };
}

function buildApiParams(filters?: BookingFilters): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (filters?.search) params.search = filters.search;
  if (filters?.status?.length === 1) params.status = toBackendStatus(filters.status[0]);
  if (filters?.status && filters.status.length > 1) {
    params.status_in = filters.status.map(toBackendStatus).join(',');
  }
  if (filters?.paymentStatus?.length === 1) params.payment_status = toBackendPaymentStatus(filters.paymentStatus[0]);
  if (filters?.checkInDate) params.check_in_date = formatDateParam(filters.checkInDate);
  if (filters?.checkOutDate) params.check_out_date = formatDateParam(filters.checkOutDate);
  if (filters?.inHouseOn) params.in_house_on = formatDateParam(filters.inHouseOn);
  if (filters?.stayFrom) params.stay_from = formatDateParam(filters.stayFrom);
  if (filters?.stayTo) params.stay_to = formatDateParam(filters.stayTo);
  // Single-edge bounds: `date_from` is `check_in >= x`, `date_to` is
  // `check_out <= y`. They exclude in-house stays that started before the
  // window — use stayFrom/stayTo for an overlap window.
  if (filters?.dateFrom) params.date_from = formatDateParam(filters.dateFrom);
  if (filters?.dateTo) params.date_to = formatDateParam(filters.dateTo);
  if (filters?.checkInTo) params.check_in_to = formatDateParam(filters.checkInTo);
  if (filters?.checkOutFrom) params.check_out_from = formatDateParam(filters.checkOutFrom);
  if (filters?.source) params.source = filters.source;
  if (filters?.excludeOutOfSeason) params.exclude_out_of_season = true;
  if (filters?.ordering) params.ordering = filters.ordering;
  if (filters?.pageSize) params.page_size = filters.pageSize;
  return params;
}

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

/**
 * Hard server ceiling for one page of `/api/v1/bookings/`
 * (`shared.pagination.StandardPagination.max_page_size`). Asking for more is
 * clamped silently, so every list surface must treat a page of exactly this
 * length as "possibly truncated" and say so instead of presenting it as whole.
 */
export const BOOKINGS_MAX_PAGE_SIZE = 200;

/** Fetch all bookings with optional filters */
export const getAllBookings = async (
  filters?: BookingFilters,
  locationId?: string | null,
): Promise<Booking[]> => {
  // location_id is passed explicitly (not left to the apiClient global) so a
  // location switch — which changes the query key and refetches during render,
  // before TenantContext's effect syncs the global — fetches the NEW location's
  // bookings instead of caching the previous location's rows under the new key.
  const params = {
    page_size: filters?.pageSize ?? 200,
    ...buildApiParams(filters),
    ...(locationId ? { location_id: locationId } : {}),
  };
  const response = await apiClient.get<{ results: BackendBookingList[]; count: number }>(
    '/api/v1/bookings/',
    { params },
  );
  return response.data.results.map(mapBackendBookingList);
};

/**
 * Server-side CSV export of the bookings matching `filters` — no page cap,
 * so a full season exports whole (PENDINGS #32). The backend gates it with
 * `location.export_bookings` (the file carries guest names) and audits
 * every export with its filters and row count; the deck adds no client-side
 * audit on top. Returns the CSV as a Blob for the caller to download.
 */
export const exportBookingsCsv = async (
  filters?: BookingFilters,
  locationId?: string | null,
): Promise<Blob> => {
  const params = {
    ...buildApiParams(filters),
    ...(locationId ? { location_id: locationId } : {}),
  };
  const response = await apiClient.get<Blob>(
    '/api/v1/bookings/export/',
    { params, responseType: 'blob' },
  );
  return response.data;
};

/**
 * Server-side count of the bookings matching `filters`.
 *
 * Asks for a single row and reads DRF's `count`, so the number is the real
 * total instead of the length of a page clamped at `BOOKINGS_MAX_PAGE_SIZE`.
 * Use this for counters and badges; use `getAllBookings` only when the rows
 * themselves are needed.
 */
export const getBookingsCount = async (
  filters?: BookingFilters,
  locationId?: string | null,
): Promise<number> => {
  const response = await apiClient.get<{ count: number }>('/api/v1/bookings/', {
    params: {
      ...buildApiParams(filters),
      page_size: 1,
      ...(locationId ? { location_id: locationId } : {}),
    },
  });
  return response.data.count ?? 0;
};

/** Fetch bookings for a specific guest */
export const getBookingsByGuestId = async (
  guestId: string,
  options?: { skipLocationFilter?: boolean; includeAdditional?: boolean },
): Promise<Booking[]> => {
  const response = await apiClient.get<{ results: BackendBookingList[] }>(
    '/api/v1/bookings/',
    {
      params: {
        ...(options?.includeAdditional ? { guest_any: guestId } : { guest: guestId }),
        // Booking history must keep cancelled trips visible — the list
        // endpoint hides cancelled bookings by default.
        include_cancelled: true,
        page_size: 200,
      },
      skipLocationFilter: options?.skipLocationFilter,
    },
  );
  return response.data.results.map(mapBackendBookingList);
};

/** Fetch bookings with pagination for infinite scroll */
export const getBookingsPaginated = async (
  filters?: BookingFilters,
  cursor?: string,
  limit: number = 20,
  locationId?: string | null,
): Promise<PaginatedBookingsResponse> => {
  let endpoint: string;
  let params: Record<string, string | number | boolean> | undefined;

  if (cursor) {
    // The cursor is the full next URL from page 1 — it already carries the
    // location_id resolved for that page, so nothing to inject here.
    endpoint = cursor;
  } else {
    // Scope page 1 to the caller's active location explicitly (overrides the
    // apiClient global, which lags the URL on a location switch — see
    // getAllBookings). Later pages inherit it via the cursor URL.
    endpoint = '/api/v1/bookings/';
    params = {
      ...buildApiParams(filters),
      page_size: limit,
      ...(locationId ? { location_id: locationId } : {}),
    };
  }

  const response = await apiClient.get<{
    results: BackendBookingList[];
    count: number;
    next: string | null;
  }>(endpoint, params ? { params } : {});

  return {
    data: response.data.results.map(mapBackendBookingList),
    nextCursor: response.data.next,
    hasMore: !!response.data.next,
    totalCount: response.data.count,
  };
};

export const getBookingGuestStays = async (filters: {
  arrivalDate?: Date;
  departureDate?: Date;
  inHouseOn?: Date;
  bookingId?: string;
  guestId?: string;
  pageSize?: number;
}): Promise<BookingGuestStayMovement[]> => {
  const params: Record<string, string | number> = {
    page_size: filters.pageSize ?? 200,
  };
  if (filters.arrivalDate) params.arrival_date = formatDateParam(filters.arrivalDate);
  if (filters.departureDate) params.departure_date = formatDateParam(filters.departureDate);
  if (filters.inHouseOn) params.in_house_on = formatDateParam(filters.inHouseOn);
  if (filters.bookingId) params.booking = filters.bookingId;
  if (filters.guestId) params.guest = filters.guestId;

  const response = await apiClient.get<{ results: BackendBookingGuestStay[] }>(
    '/api/v1/booking-guest-stays/',
    { params },
  );

  return response.data.results.map((stay) => ({
    id: stay.id,
    bookingId: stay.booking,
    bookingNumber: stay.bookingNumber ?? '',
    bookingReference: stay.bookingReference ?? null,
    guestId: stay.guest,
    guestName: stay.guestName ?? '',
    accommodationName: stay.bookingAccommodationName ?? null,
    arrivalDate: parseDateOnly(stay.arrivalDate),
    departureDate: parseDateOnly(stay.departureDate),
    checkedInAt: parseDateTime(stay.checkedInAt),
    checkedOutAt: parseDateTime(stay.checkedOutAt),
    status: stay.status,
  }));
};

/**
 * Stays still checked in after their departure date (PENDINGS #45).
 *
 * The backend does the sorting (blocking → money → the rest) and the counting,
 * so every consumer shows the same order and the KPI strip cannot drift from
 * the list under it.
 */
export const getOpenStaysPastDeparture = async (): Promise<OpenStaysResponse> => {
  const response = await apiClient.get<{
    summary: Record<string, unknown>;
    results: Record<string, unknown>[];
  }>('/api/v1/booking-guest-stays/open-past-departure/');

  const { summary, results } = response.data;
  return {
    summary: {
      total: (summary.total as number) ?? 0,
      blocking: (summary.blocking as number) ?? 0,
      withBalance: (summary.withBalance as number) ?? 0,
      ready: (summary.ready as number) ?? 0,
      balanceTotal: parseFloat((summary.balanceTotal as string) ?? '0') || 0,
      oldestDeparture: parseDateOnly(summary.oldestDeparture as string | null),
      byLocation: (summary.byLocation as Record<string, number>) ?? {},
    },
    results: results.map((row) => ({
      id: row.id as string,
      guestId: row.guestId as string,
      personId: (row.personId as string) ?? '',
      guestName: (row.guestName as string) ?? '',
      bookingId: row.bookingId as string,
      bookingNumber: (row.bookingNumber as string) ?? '',
      locationId: (row.locationId as string) ?? '',
      locationName: (row.locationName as string) ?? '',
      accommodationName: (row.accommodationName as string | null) ?? null,
      arrivalDate: parseDateOnly(row.arrivalDate as string | null),
      departureDate: parseDateOnly(row.departureDate as string | null),
      daysOpen: (row.daysOpen as number) ?? 0,
      folioBalance: parseFloat((row.folioBalance as string) ?? '0') || 0,
      integrationDueAmount: parseFloat((row.integrationDueAmount as string) ?? '0') || 0,
      isIntegrationOwned: Boolean(row.isIntegrationOwned),
      blocksStayId: (row.blocksStayId as string | null) ?? null,
      blocksBookingNumber: (row.blocksBookingNumber as string | null) ?? null,
    })),
  };
};

export const updateBookingGuestStay = async (
  id: string,
  data: Partial<{
    status: BookingGuestStayMovement['status'];
    checkedInAt: Date | null;
    checkedOutAt: Date | null;
  }>,
): Promise<BookingGuestStayMovement> => {
  const payload: Record<string, string | null> = {};
  if (data.status !== undefined) payload.status = data.status;
  if (data.checkedInAt !== undefined) payload.checked_in_at = data.checkedInAt ? data.checkedInAt.toISOString() : null;
  if (data.checkedOutAt !== undefined) payload.checked_out_at = data.checkedOutAt ? data.checkedOutAt.toISOString() : null;

  const response = await apiClient.patch<BackendBookingGuestStay>(
    `/api/v1/booking-guest-stays/${id}/`,
    payload,
  );

  const stay = response.data;
  return {
    id: stay.id,
    bookingId: stay.booking,
    bookingNumber: stay.bookingNumber ?? '',
    bookingReference: stay.bookingReference ?? null,
    guestId: stay.guest,
    guestName: stay.guestName ?? '',
    accommodationName: stay.bookingAccommodationName ?? null,
    arrivalDate: parseDateOnly(stay.arrivalDate),
    departureDate: parseDateOnly(stay.departureDate),
    checkedInAt: parseDateTime(stay.checkedInAt),
    checkedOutAt: parseDateTime(stay.checkedOutAt),
    status: stay.status,
  };
};

/** Fetch booking by ID */
export const getBookingById = async (id: string): Promise<Booking> => {
  const response = await apiClient.get<BackendBookingDetail>(`/api/v1/bookings/${id}/`);
  return mapBackendBooking(response.data);
};

/** Fetch booking detail with enriched guest and service data */
export const getBookingDetail = async (id: string): Promise<BookingDetail> => {
  const response = await apiClient.get<BackendBookingDetail>(`/api/v1/bookings/${id}/`);
  const b = response.data;
  const booking = mapBackendBooking(b);
  const additionalGuests = b.additionalGuests ?? b.additional_guests ?? [];
  const staysByGuestId = new Map((booking.guestStays ?? []).map((stay) => [stay.guestId, stay]));
  const accommodations = booking.accommodations ?? [];
  const primaryAccommodation = accommodations[0];
  const integrationLines = mapIntegrationLines(b.integrationLines);
  const integrationFinancialEvents = mapIntegrationFinancialEvents(
    b.integrationFinancialEvents,
  );

  const occupantGuestIds = new Set(staysByGuestId.keys());
  const guestDetails = buildBookingGuests(b.guest, b.guestName || undefined, additionalGuests, occupantGuestIds).map((guest) => {
    const guestName = guest.guestName ?? '';
    const stay = staysByGuestId.get(guest.guestId);
    return {
      guestId: guest.guestId,
      isOwner: guest.isOwner,
      stayId: stay?.id,
      stayStatus: stay?.status,
      accommodationId: stay?.accommodationId ?? null,
      accommodationName: stay?.accommodationName ?? null,
      arrivalDate: stay?.arrivalDate ?? null,
      departureDate: stay?.departureDate ?? null,
      checkedInAt: stay?.checkedInAt ?? null,
      checkedOutAt: stay?.checkedOutAt ?? null,
      waiverStatus: stay?.waiverStatus,
      waiverSignedAt: stay?.waiverSignedAt ?? null,
      waiverDocumentAvailable: stay?.waiverDocumentAvailable,
      guestInfo: {
        firstName: guestName.split(' ')[0] ?? '',
        lastName: guestName.split(' ').slice(1).join(' ') ?? '',
        email: guest.guestEmail ?? '',
      },
      services: [] as Array<{ id: string; name: string; price: number }>,
    };
  });

  const booker = b.guest
    ? {
        guestId: b.guest,
        name: b.guestName || '',
        isOccupant: occupantGuestIds.has(b.guest),
      }
    : undefined;

  return {
    ...booking,
    integrationLines,
    integrationFinancialEvents,
    guestDetails,
    booker,
    accommodationInfo: accommodations.length > 0
      ? {
          name: booking.accommodationSummary || primaryAccommodation?.name || '',
          type: accommodations.length > 1 ? 'multiple' : (primaryAccommodation?.roomTypeName || 'room'),
          roomNumber: primaryAccommodation?.roomName || undefined,
          items: accommodations,
        }
      : b.roomName
        ? { name: b.roomName, type: 'room', items: [] }
        : { name: '', type: 'room', items: [] },
    sharedServices: integrationLines
      .filter((line) => line.productType === 'service' || line.productType === 'activity')
      .map((line) => ({
        id: line.id,
        name: line.title || line.description || 'Bookinglayer line',
        price: line.totalPrice,
      })),
  };
};

/** Create new booking */
export const createBooking = async (data: BookingFormData): Promise<Booking> => {
  const guestId = data.guests?.[0]?.guestId;
  const payload = {
    guest: guestId,
    room: data.accommodationId || null,
    check_in: formatLocalDate(data.checkInDate),
    check_out: formatLocalDate(data.checkOutDate),
    adults: data.adults ?? 1,
    children: data.children ?? 0,
    total_amount: data.totalAmount ?? 0,
    paid_amount: 0,
    status: 'pending',
    special_requests: '',
    notes: data.notes || '',
  };
  const response = await apiClient.post<BackendBookingDetail>('/api/v1/bookings/', payload);
  return mapBackendBooking(response.data);
};

/** Create booking from person-first form selection */
export const createBookingFromPeople = async (data: BookingFormDataInput): Promise<Booking> => {
  const payload = {
    location: data.locationId,
    room: data.accommodationIds[0] || null,
    accommodation_ids: data.accommodationIds,
    check_in: formatLocalDate(data.checkInDate),
    check_out: formatLocalDate(data.checkOutDate),
    adults: data.guests.length || 1,
    children: 0,
    total_amount: data.totalAmount ?? 0,
    paid_amount: 0,
    status: 'pending',
    special_requests: '',
    notes: data.notes || '',
    season_override_reason: data.seasonOverrideReason || '',
    external_reference: data.referenceId || '',
    booking_type: data.bookingType ?? 'guest',
    sponsored_by: data.sponsoredBy ?? null,
    daily_rate: data.dailyRate ?? null,
    guests: data.guests.map((guest) => ({
      person_id: guest.personId ?? null,
      guest_id: guest.guestId.startsWith('draft:') ? null : guest.guestId,
      is_owner: guest.isOwner,
    })),
  };
  const response = await apiClient.post<BackendBookingDetail>(
    '/api/v1/bookings/create-from-people/',
    payload,
  );
  return mapBackendBooking(response.data);
};

// ── Walk-in intake ─────────────────────────────────────────────────────────
//
// POST /api/v1/bookings/walk-in/ — single-step walk-in flow used by the
// shared `WalkInDialog`. Backend dedups Person by email + document_number
// and returns 409 with both candidates when the keys collide on
// different rows. The 409 payload is surfaced as `WalkInPersonConflict`
// so the dialog can render the choice and re-submit with a chosen
// `linkToPersonId`.

export interface WalkInIdentityInput {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  phoneCountryIso?: string;
  phoneDialCode?: string;
  nationalityIso?: string;
  documentType?: '' | 'passport' | 'id_card' | 'driving_license' | 'other';
  documentNumber?: string;
  documentCountryIso?: string;
  /** ISO date YYYY-MM-DD; empty when unset. */
  documentExpiration?: string;
}

export interface WalkInBookingInput {
  locationId: string;
  identity: WalkInIdentityInput;
  checkInDate: Date;
  checkOutDate: Date;
  adults: number;
  children: number;
  accommodationId?: string | null;
  dailyRate?: number | null;
  totalAmount?: number;
  notes?: string;
  checkInNow?: boolean;
  depositAmount?: number;
  depositMethod?: string;
  /** Operator-chosen Person id, only used when retrying a 409. */
  linkToPersonId?: string | null;
}

export interface WalkInPersonMatch {
  matched_on: 'email' | 'document';
  is_stronger_match: boolean;
  person: {
    id: string;
    name: string;
    email?: string;
    primary_email?: string | null;
    primary_document?: { document_type?: string; document_number?: string } | null;
    primary_phone?: { phone?: string } | null;
    nationality_iso?: string;
    avatar_url?: string;
  };
}

export interface WalkInPersonConflict {
  detail: string;
  matches: WalkInPersonMatch[];
}

export interface WalkInBookingResult {
  booking: Booking;
  matchedExistingPerson: boolean;
  personId: string;
}

/** Returned when the backend responds 409 — operator must pick a match. */
export class WalkInPersonConflictError extends Error {
  constructor(public readonly conflict: WalkInPersonConflict) {
    super(conflict.detail);
    this.name = 'WalkInPersonConflictError';
  }
}

const toYmd = (d: Date) => formatLocalDate(d);

export const createWalkInBooking = async (
  data: WalkInBookingInput,
): Promise<WalkInBookingResult> => {
  const payload = {
    location: data.locationId,
    identity: {
      first_name: data.identity.firstName,
      last_name: data.identity.lastName,
      email: data.identity.email ?? '',
      phone: data.identity.phone ?? '',
      phone_country_iso: data.identity.phoneCountryIso ?? '',
      phone_country_code: data.identity.phoneDialCode ?? '',
      nationality_iso: data.identity.nationalityIso ?? '',
      document_type: data.identity.documentType ?? '',
      document_number: data.identity.documentNumber ?? '',
      document_country_iso: data.identity.documentCountryIso ?? '',
      document_expiration: data.identity.documentExpiration || null,
    },
    check_in: toYmd(data.checkInDate),
    check_out: toYmd(data.checkOutDate),
    adults: data.adults,
    children: data.children,
    accommodation_id: data.accommodationId ?? null,
    daily_rate: data.dailyRate ?? null,
    total_amount: data.totalAmount ?? 0,
    notes: data.notes ?? '',
    check_in_now: data.checkInNow ?? false,
    deposit_amount: data.depositAmount ?? 0,
    deposit_method: data.depositMethod ?? '',
    link_to_person_id: data.linkToPersonId ?? null,
  };

  try {
    // apiClient camelizes every response, so the backend's walk_in_* keys
    // arrive as walkIn* — reading snake_case here crashed the SUCCESS path
    // and pushed operators into retrying an already-created booking.
    const response = await apiClient.post<
      BackendBookingDetail & {
        walkInPerson: { id: string };
        walkInWasExistingMatch: boolean;
      }
    >('/api/v1/bookings/walk-in/', payload);
    return {
      booking: mapBackendBooking(response.data),
      matchedExistingPerson: response.data.walkInWasExistingMatch,
      personId: response.data.walkInPerson.id,
    };
  } catch (err) {
    // apiClient throws ApiError with status + data at the top level
    const apiErr = err as import('@/types/global').ApiError;
    if (apiErr?.status === 409 && apiErr.data) {
      const body = apiErr.data as WalkInPersonConflict;
      if (body.matches) {
        throw new WalkInPersonConflictError(body);
      }
    }
    throw err;
  }
};

/** Update booking */
export const updateBooking = async (id: string, data: Partial<BookingFormData>): Promise<Booking> => {
  const payload: Record<string, unknown> = {};
  if (data.checkInDate) payload.check_in = formatLocalDate(data.checkInDate);
  if (data.checkOutDate) payload.check_out = formatLocalDate(data.checkOutDate);
  if (data.accommodationId !== undefined) payload.room = data.accommodationId || null;
  if (data.accommodationIds !== undefined) payload.accommodation_ids_input = data.accommodationIds;
  if (data.notes !== undefined) payload.notes = data.notes;
  if (data.totalAmount !== undefined) payload.total_amount = data.totalAmount;
  if (data.adults !== undefined) payload.adults = data.adults;
  if (data.children !== undefined) payload.children = data.children;
  if ((data as Record<string, unknown>).seasonOverrideReason !== undefined) payload.season_override_reason_input = (data as Record<string, unknown>).seasonOverrideReason;

  const response = await apiClient.patch<BackendBookingDetail>(`/api/v1/bookings/${id}/`, payload);
  return mapBackendBooking(response.data);
};

/** Change booking status — uses backend checkin/checkout actions or status PATCH */
export const changeBookingStatus = async (id: string, newStatus: BookingStatus): Promise<Booking> => {
  let response: { data: BackendBookingDetail };
  if (newStatus === 'checked-in') {
    response = await apiClient.post<BackendBookingDetail>(`/api/v1/bookings/${id}/checkin/`, {});
  } else if (newStatus === 'checked-out') {
    response = await apiClient.post<BackendBookingDetail>(`/api/v1/bookings/${id}/checkout/`, {});
  } else {
    response = await apiClient.patch<BackendBookingDetail>(
      `/api/v1/bookings/${id}/`,
      { status: toBackendStatus(newStatus) },
    );
  }
  return mapBackendBooking(response.data);
};

/** Cancel booking — populates dedicated cancellation fields. Backend
 *  auto-stamps cancelled_at on the status transition. */
export const cancelBooking = async (request: CancellationRequest): Promise<Booking> => {
  const payload: Record<string, unknown> = {
    status: 'cancelled',
    cancellation_reason: request.reason,
  };
  if (request.refundAmount !== undefined) {
    payload.cancellation_fee = request.refundAmount;
  }
  const response = await apiClient.patch<BackendBookingDetail>(
    `/api/v1/bookings/${request.bookingId}/`,
    payload,
  );
  return mapBackendBooking(response.data);
};

/** Get audit history for booking */
export const getBookingAuditHistory = async (bookingId: string): Promise<BookingAuditEntry[]> => {
  const response = await apiClient.get<{ results: Array<{
    id: string;
    booking: string;
    action: string;
    field: string;
    oldValue: string;
    newValue: string;
    performedBy: string;
    performedByName: string;
    metadata: Record<string, unknown>;
    createdAt: string;
  }> }>('/api/v1/booking-audit/', { params: { booking: bookingId, page_size: 100 } });
  return response.data.results.map(e => ({
    id: e.id,
    bookingId: e.booking,
    action: e.action.replace('_', '-') as BookingAuditAction,
    field: e.field || undefined,
    oldValue: e.oldValue || undefined,
    newValue: e.newValue || undefined,
    performedBy: e.performedBy,
    performedByName: e.performedByName,
    timestamp: new Date(e.createdAt),
    createdAt: new Date(e.createdAt),
    updatedAt: new Date(e.createdAt),
  }));
};

// ---------------------------------------------------------------------------
// Folio services
// ---------------------------------------------------------------------------

/** Get or create the Folio for a booking (backend auto-creates on GET) */
export const getFolioByBookingId = async (
  bookingId: string,
  options?: { skipLocationFilter?: boolean },
): Promise<Folio | null> => {
  const response = await apiClient.get<{ results: Folio[] }>(
    '/api/v1/folios/',
    {
      params: { booking: bookingId },
      skipLocationFilter: options?.skipLocationFilter,
    },
  );
  return response.data.results[0] ?? null;
};

/** Get payments for a folio */
export const getFolioPayments = async (folioId: string): Promise<FolioPayment[]> => {
  const response = await apiClient.get<FolioPayment[]>(
    `/api/v1/folios/${folioId}/payments/`,
  );
  return response.data;
};

/** Record a payment against a folio */
export const recordFolioPayment = async (input: RecordFolioPaymentInput): Promise<FolioPayment> => {
  const response = await apiClient.post<FolioPayment>(
    `/api/v1/folios/${input.folioId}/payments/`,
    {
      amount: input.amount,
      method: input.method,
      paid_by_person: input.paidByPersonId ?? null,
      paid_by_name: input.paidByName ?? '',
      paid_by_notes: input.paidByNotes ?? '',
      notes: input.notes ?? '',
      processor_ref: input.processorRef ?? '',
      processor_last4: input.processorLast4 ?? '',
    },
  );
  return response.data;
};

/** Add a manual charge to a folio */
export const addFolioCharge = async (input: AddFolioChargeInput): Promise<Folio> => {
  await apiClient.post(
    '/api/v1/folio-charges/',
    {
      folio: input.folioId,
      description: input.description,
      amount: input.amount,
      category: input.category ?? 'other',
      quantity: input.quantity ?? 1,
      unit_price: input.unitPrice ?? input.amount,
      incurred_by: input.incurredBy ?? null,
    },
  );
  // Refetch the folio to get updated totals
  const folioResp = await apiClient.get<Folio>(`/api/v1/folios/${input.folioId}/`);
  return folioResp.data;
};

/** Close a folio */
export const closeFolio = async (folioId: string): Promise<Folio> => {
  const response = await apiClient.post<Folio>(`/api/v1/folios/${folioId}/close/`);
  return response.data;
};

/** Void a folio charge */
export const voidFolioCharge = async (chargeId: string, reason: string): Promise<void> => {
  await apiClient.post(`/api/v1/folio-charges/${chargeId}/void/`, { reason });
};

/**
 * Link / unlink a folio charge from the Bookinglayer outbound push.
 * Unlinking removes the charge from the booking's per-category BL amendment
 * (the local folio balance is untouched); re-linking adds it back. Only valid
 * for charges of BL-sourced bookings — the backend rejects the rest.
 */
export const setFolioChargeBlLink = async (chargeId: string, linked: boolean): Promise<void> => {
  await apiClient.post(`/api/v1/folio-charges/${chargeId}/bl-link/`, { linked });
};

/** Get a consolidated expense statement for a booking */
export const getBookingExpenseStatement = async (bookingId: string): Promise<ExpenseStatement> => {
  const response = await apiClient.get<ExpenseStatement>(
    `/api/v1/bookings/${bookingId}/statement/`,
    { skipLocationFilter: true },
  );
  return response.data;
};

/**
 * Unified booking settlement — pay a selected mix of folio + per-guest charge
 * lines in ONE atomic action. The backend records the folio portion as a
 * single folio payment and settles each selected guest charge.
 * Returns the created payments + the refreshed statement.
 */
export const settleBookingCharges = async (
  bookingId: string,
  input: SettleBookingChargesInput,
): Promise<{ payments: FolioPayment[]; statement: ExpenseStatement }> => {
  const response = await apiClient.post<{ payments: FolioPayment[]; statement: ExpenseStatement }>(
    `/api/v1/bookings/${bookingId}/settle/`,
    {
      folio_amount: input.folioAmount ?? null,
      guest_charge_ids: input.guestChargeIds ?? [],
      method: input.method,
      paid_by_person: input.paidByPersonId ?? null,
      paid_by_name: input.paidByName ?? '',
      paid_by_notes: input.paidByNotes ?? '',
      notes: input.notes ?? '',
    },
  );
  return response.data;
};

/** Pay by amount, split between payers, against the folio balance. Each split
 *  becomes one folio payment attributed to its payer; amounts must sum to
 *  `amount`. Maps to POST /api/v1/bookings/{id}/settle_split/. */
export const settleBookingSplit = async (
  bookingId: string,
  input: SettleBookingSplitInput,
): Promise<{ payments: FolioPayment[]; statement: ExpenseStatement }> => {
  const response = await apiClient.post<{ payments: FolioPayment[]; statement: ExpenseStatement }>(
    `/api/v1/bookings/${bookingId}/settle_split/`,
    {
      amount: input.amount,
      splits: input.splits.map((s) => ({
        amount: s.amount,
        method: s.method,
        paid_by_person: s.paidByPersonId ?? null,
        paid_by_name: s.paidByName ?? '',
      })),
      notes: input.notes ?? '',
    },
  );
  return response.data;
};
